"use client";

import { useEffect, useRef, useState } from "react";
import Link from "next/link";
import { usePathname } from "next/navigation";
import { useAuth } from "@/contexts/AuthContext";
import { START_HREF } from "@/lib/routes";
import UserButton from "@/components/auth/UserButton";
import ThemeToggle from "@/components/ui/ThemeToggle";

/**
 * Shared navigation used across every public-facing page.
 *
 * - Scroll-activated backdrop (matches the landing page's original behaviour)
 * - Full nav link set so clicking "The score" doesn't leave the user stranded
 *   on a page with no way back into other sections
 * - Mobile (<md): section links collapse into a hamburger-triggered full-screen
 *   panel — previously they were simply hidden, which made Pricing unreachable
 *   on phones. The panel carries the links, the EN/NL toggle, Sign in and the
 *   primary CTA; it locks body scroll, traps focus, closes on Escape, on route
 *   change, and on same-page hash navigation.
 * - ONE menu per surface. On mobile the hamburger panel is the single menu:
 *   signed-in users get their account links (Dashboard / Saved plots / Profile
 *   / Billing), an identity block and Sign out inside it, and the avatar
 *   button is hidden (two adjacent triggers opening different-looking menus
 *   was confusing). On desktop the avatar dropdown (UserButton variant="site",
 *   styled to match this editorial surface) is the single account menu.
 * - Auth-aware CTA:
 *     signed out → "Start free" / "Begin gratis" → START_HREF (i.e. /signup).
 *       Never /app: it's auth-walled, so it dumps signed-out visitors on a
 *       sign-IN form for an account they don't have. See src/lib/routes.ts.
 *     signed in  → "Open the map" / "Open de kaart" → /app, plus a Dashboard link
 * - EN/NL toggle — accepts an explicit `altHref` per page so the switch
 *   resolves to the right localized URL (pages often have asymmetric NL routes)
 *
 * Each page passes a locale and the URL to its counterpart in the other
 * language. Sticky-by-default, scroll-activated backdrop for visual
 * continuity with the landing page.
 *
 * Structure note: the translucent/blurred background lives on an inner div,
 * NOT on the fixed <nav> itself — `backdrop-filter` turns an element into a
 * containing block for fixed-position descendants, which would collapse the
 * full-screen mobile panel to the height of the bar.
 */

export interface SiteNavProps {
  locale: "en" | "nl";
  /** URL to switch to when the user toggles language. */
  altHref: string;
  /** Which nav link to mark as current. */
  active?: "score" | "preview" | "who" | "pricing" | null;
  /** When true, nav floats on top of a dark hero instead of over white content. Default false. */
  onDarkHero?: boolean;
  /** "app" trims the marketing section links (score/preview/who) for the
   *  signed-in /account shell — only Pricing stays reachable (for upgrades).
   *  Default "marketing" keeps the full public nav. */
  context?: "marketing" | "app";
}

const NAV_COPY = {
  en: {
    score: "The score",
    preview: "Preview",
    who: "Who it's for",
    pricing: "Pricing",
    signIn: "Sign in",
    signOut: "Sign out",
    cta: "Start free",
    ctaSignedIn: "Open the map",
    menu: "Menu",
    close: "Close",
    language: "Language",
    theme: "Theme",
    yourAccount: "Your account",
    dashboard: "Dashboard",
    savedPlots: "Saved plots",
    profile: "Profile",
    billing: "Billing",
  },
  nl: {
    score: "De score",
    preview: "Preview",
    who: "Voor wie",
    pricing: "Prijzen",
    signIn: "Inloggen",
    signOut: "Uitloggen",
    cta: "Begin gratis",
    ctaSignedIn: "Open de kaart",
    menu: "Menu",
    close: "Sluiten",
    language: "Taal",
    theme: "Thema",
    yourAccount: "Jouw account",
    dashboard: "Dashboard",
    savedPlots: "Opgeslagen percelen",
    profile: "Profiel",
    billing: "Facturatie",
  },
} as const;

/** Hairline used inside the mobile panel. Goes through the `--hairline` theme
 *  composite, NOT a literal: opening the menu clears the data-theme override
 *  above, so the panel paints `bg-brand-forest` = paper in light mode, and the
 *  frozen dark value this used to hold (linen at 8%) left every divider
 *  invisible there. `--hairline` carries the light counterpart. */
const HAIRLINE = "border-[var(--hairline)]";

export default function SiteNav({ locale, altHref, active = null, context = "marketing", onDarkHero = false }: SiteNavProps) {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const navRef = useRef<HTMLElement>(null);
  const menuButtonRef = useRef<HTMLButtonElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const { user, loading: authLoading, signOut } = useAuth();
  const pathname = usePathname();
  const copy = NAV_COPY[locale];
  const signedIn = !!user && !authLoading;

  // Account destinations — surfaced in the mobile panel when signed in, so
  // the hamburger is the one menu on phones (the avatar dropdown is desktop
  // only). Active state derives from the pathname; the /account root is the
  // profile page, so it needs an exact match.
  const accountLinks = [
    { key: "dashboard", href: "/account/dashboard", label: copy.dashboard },
    { key: "plots", href: "/account/plots", label: copy.savedPlots },
    { key: "profile", href: "/account", label: copy.profile },
    { key: "billing", href: "/account/billing", label: copy.billing },
  ] as const;
  const isAccountPath = (href: string) =>
    href === "/account" ? pathname === "/account" : pathname?.startsWith(href) ?? false;

  // Index shown next to Pricing in the mobile panel. Marketing panel lists
  // 01–03 sections above it; the signed-in account panel lists 01–04 account
  // rows above it. Signed out in the app shell there's nothing to count.
  const pricingIndex = context === "marketing" ? "04" : signedIn ? "05" : null;

  const displayName = (user?.user_metadata?.full_name as string | undefined) || user?.email || "";
  const initials = (() => {
    if (!user) return "";
    const full = user.user_metadata?.full_name as string | undefined;
    if (full) {
      const parts = full.split(" ");
      return ((parts[0]?.[0] || "") + (parts[1]?.[0] || "")).toUpperCase();
    }
    return user.email?.[0]?.toUpperCase() || "?";
  })();

  useEffect(() => {
    const handle = () => setScrolled(window.scrollY > 40);
    handle(); // set initial state on mount
    window.addEventListener("scroll", handle, { passive: true });
    return () => window.removeEventListener("scroll", handle);
  }, []);

  // Close the mobile panel whenever the route changes (link navigation).
  useEffect(() => {
    setMenuOpen(false);
  }, [pathname]);

  // Close it when the viewport crosses into desktop — otherwise the scroll
  // lock would linger with the panel display:none'd by md:hidden.
  useEffect(() => {
    if (!menuOpen) return;
    const mq = window.matchMedia("(min-width: 768px)");
    const onChange = () => { if (mq.matches) setMenuOpen(false); };
    onChange();
    mq.addEventListener("change", onChange);
    return () => mq.removeEventListener("change", onChange);
  }, [menuOpen]);

  // Body scroll lock while the panel is open. Inline style with priority
  // "important" — landing.css forces `overflow: visible !important` on the
  // landing html/body, and an inline !important is the one thing that wins.
  useEffect(() => {
    if (!menuOpen) return;
    const html = document.documentElement;
    const body = document.body;
    html.style.setProperty("overflow", "hidden", "important");
    body.style.setProperty("overflow", "hidden", "important");
    return () => {
      html.style.removeProperty("overflow");
      body.style.removeProperty("overflow");
    };
  }, [menuOpen]);

  // Focus management: move focus into the panel on open, restore it to the
  // hamburger on close, close on Escape, and keep Tab cycling inside the
  // <nav> (bar + panel are the only interactive things visible while open).
  useEffect(() => {
    if (!menuOpen) return;
    const nav = navRef.current;
    const panel = panelRef.current;
    const previouslyFocused = document.activeElement as HTMLElement | null;
    const focusables = () =>
      Array.from(
        nav?.querySelectorAll<HTMLElement>('a[href], button:not([disabled])') ?? []
      ).filter((el) => el.offsetWidth > 0 || el.offsetHeight > 0);

    // First link in the panel gets focus so screen readers land in the menu.
    panel?.querySelector<HTMLElement>('a[href], button:not([disabled])')?.focus();

    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        e.preventDefault();
        setMenuOpen(false);
        menuButtonRef.current?.focus();
        return;
      }
      if (e.key !== "Tab") return;
      const els = focusables();
      if (els.length === 0) return;
      const first = els[0];
      const last = els[els.length - 1];
      const focused = document.activeElement as HTMLElement | null;
      if (e.shiftKey) {
        if (focused === first || !focused || !nav?.contains(focused)) {
          e.preventDefault();
          last.focus();
        }
      } else if (focused === last || !focused || !nav?.contains(focused)) {
        e.preventDefault();
        first.focus();
      }
    };
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("keydown", onKeyDown);
      // Restore focus to the trigger unless focus already moved somewhere
      // sensible (e.g. a link the user just clicked before navigation).
      if (document.activeElement === document.body) {
        previouslyFocused?.focus?.();
      }
    };
  }, [menuOpen]);

  // Link paths — "Who it's for" is a hash anchor on the landing page; from
  // any other page it needs to resolve to "/#who". Preview is its own page.
  const scorePath = locale === "nl" ? "/nl/methodology" : "/methodology";
  const previewPath = locale === "nl" ? "/nl/preview" : "/preview";
  const pricingPath = locale === "nl" ? "/nl/pricing" : "/pricing";
  const homePath = locale === "nl" ? "/nl" : "/";
  const whoPath = `${homePath}#who`;
  const appPath = "/app";

  // Same-page hash navigation. When we're already on the landing, a Link to
  // "/#who" is a same-URL navigation that Next.js won't reliably
  // re-scroll for (especially when the hash is already in the URL). Scroll
  // directly and sync the hash without a router round-trip. From every other
  // route we fall through to the normal Link navigation, and the landing
  // page's own hash effect performs the scroll after mount.
  const hashNavClick =
    (hash: "who") => (e: React.MouseEvent<HTMLAnchorElement>) => {
      if (pathname !== homePath) return;
      // Preserve native anchor semantics for new-tab / modified clicks.
      if (e.defaultPrevented || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
      const el = document.getElementById(hash);
      if (!el) return;
      e.preventDefault();
      el.scrollIntoView({ behavior: "smooth" });
      history.replaceState(null, "", `${homePath}#${hash}`);
    };

  // Panel variant of the same handler: close the menu first (releasing the
  // scroll lock), then scroll on the next tick so the lock is gone by the
  // time the smooth scroll starts. Cross-page clicks just close and let the
  // Link navigate.
  const panelHashClick =
    (hash: "who") => (e: React.MouseEvent<HTMLAnchorElement>) => {
      setMenuOpen(false);
      if (pathname !== homePath) return;
      if (e.defaultPrevented || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
      const el = document.getElementById(hash);
      if (!el) return;
      e.preventDefault();
      history.replaceState(null, "", `${homePath}#${hash}`);
      window.setTimeout(() => el.scrollIntoView({ behavior: "smooth" }), 0);
    };

  const closeMenu = () => setMenuOpen(false);

  return (
    // Theme note: at the top of the page this bar is TRANSPARENT and floats over
    // whatever the page puts there. On a page whose hero is a framed DARK
    // surface it must use dark-theme ink, or in light mode its links render as
    // dark text on a dark photograph and disappear. Once `scrolled` (or the
    // mobile menu is open) the bar paints its own page-coloured backdrop and
    // must switch back to the ambient theme so the ink matches that backdrop.
    //
    // `onDarkHero` gates that override and DEFAULTS TO FALSE, because only two
    // hosts actually put a dark surface behind the bar — HeroExperience (the
    // landing) and MethodologyHero3D — and both already declare their own
    // data-theme="dark". Every other host (pricing, data-sources, founder, the
    // audience pages, the ~880 buurt guides, and the whole /account shell) is
    // an ambient-themed page. Forcing dark ink there put near-white #F0EDE5
    // ink on #F6F5F0 paper — 1.07:1, invisible until you scrolled 40px. The
    // default is the legible direction: a host that forgets the flag is merely
    // plain, never unreadable.
    <nav
      ref={navRef}
      data-theme={onDarkHero && !scrolled && !menuOpen ? "dark" : undefined}
      className="fixed top-0 left-0 right-0 z-50"
    >
      {/* Bar backdrop lives on this inner div — see structure note above. */}
      <div
        className={`transition-all duration-300 ${
          menuOpen
            ? `bg-brand-forest border-b ${HAIRLINE}`
            : scrolled
              ? "bg-brand-forest/85 backdrop-blur-xl border-b border-brand-border"
              : ""
        }`}
      >
        <div className="max-w-[1120px] mx-auto px-5 sm:px-8 flex items-center justify-between h-14 sm:h-16">
          {/* Wordmark → home */}
          <Link
            href={homePath}
            onClick={closeMenu}
            className="font-display text-[1.35rem] sm:text-[1.5rem] font-semibold text-brand-linen tracking-[0.02em]"
          >
            PlotScore
          </Link>

          {/* Right cluster */}
          <div className="flex items-center gap-3 sm:gap-4 md:gap-7">
            {/* Marketing section links — hidden in the signed-in account shell
                (context="app") so the dashboard nav doesn't point back out to
                marketing. Pricing stays, so upgrading is always one click away. */}
            {context === "marketing" && (
              <>
                <NavItem href={scorePath} label={copy.score} active={active === "score"} />
                <NavItem href={previewPath} label={copy.preview} active={active === "preview"} />
                <NavItem href={whoPath} label={copy.who} active={active === "who"} onClick={hashNavClick("who")} />
              </>
            )}
            <NavItem href={pricingPath} label={copy.pricing} active={active === "pricing"} />

            {/* Signed-in members get a first-class Dashboard link in the bar —
                their primary destination shouldn't hide behind the avatar. */}
            {signedIn && (
              <NavItem
                href="/account/dashboard"
                label={copy.dashboard}
                active={isAccountPath("/account/dashboard")}
              />
            )}

            {/* Language toggle — desktop only; the mobile panel has its own. */}
            <LangSwitch locale={locale} altHref={altHref} className="hidden md:flex" />

            {/* Theme toggle — for EVERY visitor, signed in or not. The only
                other toggles live behind the auth wall (/app toolbar, account
                settings), so anonymous readers had no way to leave dark mode.
                Cookie-backed via ThemeProvider; no account needed. */}
            <ThemeToggle variant="icon" className="hidden md:flex" />

            {/* Auth entry points. Signed-in users get the primary CTA + an
                avatar menu on desktop (standard SaaS pattern — dashboard/
                plots/profile/billing/sign-out live in the dropdown, styled to
                match this surface via variant="site"). On mobile the avatar
                is hidden — the hamburger panel carries the account links, so
                phones have exactly one menu. Signed-out users get a text
                Sign in link plus the Start free CTA; on mobile the text link
                moves into the hamburger panel and the CTA stays in the bar. */}
            {signedIn ? (
              <>
                <Link
                  href={appPath}
                  className="inline-flex items-center px-4 py-2.5 sm:px-5 bg-brand-meadow text-brand-forest text-[0.8rem] sm:text-sm font-medium rounded-md hover:bg-brand-meadow-light active:scale-[0.98] transition-all whitespace-nowrap"
                >
                  {copy.ctaSignedIn}
                </Link>
                <span className="hidden md:block">
                  <UserButton variant="site" />
                </span>
              </>
            ) : (
              <>
                <Link
                  href="/signin"
                  className="hidden md:inline text-sm text-brand-sage hover:text-brand-linen transition-colors whitespace-nowrap"
                >
                  {copy.signIn}
                </Link>
                <Link
                  href={START_HREF}
                  className="inline-flex items-center px-4 py-2.5 sm:px-5 bg-brand-meadow text-brand-forest text-[0.8rem] sm:text-sm font-medium rounded-md hover:bg-brand-meadow-light active:scale-[0.98] transition-all whitespace-nowrap"
                >
                  {copy.cta}
                </Link>
              </>
            )}

            {/* Hamburger — mobile only. 44px square tap target. */}
            <button
              ref={menuButtonRef}
              type="button"
              onClick={() => setMenuOpen((o) => !o)}
              aria-expanded={menuOpen}
              aria-controls="site-nav-menu"
              aria-label={menuOpen ? copy.close : copy.menu}
              className="md:hidden inline-flex h-11 w-11 -mr-2 items-center justify-center text-brand-linen"
            >
              <span aria-hidden className="relative block h-[15px] w-5">
                <span
                  className={`absolute left-0 top-0 block h-px w-5 bg-current transition-transform duration-200 ${
                    menuOpen ? "translate-y-[7px] rotate-45" : ""
                  }`}
                />
                <span
                  className={`absolute left-0 top-[7px] block h-px w-5 bg-current transition-opacity duration-200 ${
                    menuOpen ? "opacity-0" : ""
                  }`}
                />
                <span
                  className={`absolute left-0 bottom-0 block h-px w-5 bg-current transition-transform duration-200 ${
                    menuOpen ? "-translate-y-[7px] -rotate-45" : ""
                  }`}
                />
              </span>
            </button>
          </div>
        </div>
      </div>

      {/* ── Mobile menu panel ── */}
      {menuOpen && (
        <div
          ref={panelRef}
          id="site-nav-menu"
          role="dialog"
          aria-modal="true"
          aria-label={copy.menu}
          className="md:hidden fixed inset-0 top-14 sm:top-16 bg-brand-forest overflow-y-auto overscroll-contain"
        >
          <div className="flex min-h-full flex-col px-5 pt-8 pb-[max(1.5rem,env(safe-area-inset-bottom))]">
            {/* Mono micro-label, matching the landing's section eyebrows */}
            <p className="font-data text-[0.65rem] uppercase tracking-[0.22em] text-brand-muted mb-3">
              {copy.menu}
            </p>

            <div className={`border-t ${HAIRLINE}`}>
              {context === "marketing" && (
                <>
                  <MobileNavLink index="01" href={scorePath} label={copy.score} active={active === "score"} onClick={closeMenu} />
                  <MobileNavLink index="02" href={previewPath} label={copy.preview} active={active === "preview"} onClick={closeMenu} />
                  <MobileNavLink index="03" href={whoPath} label={copy.who} active={active === "who"} onClick={panelHashClick("who")} />
                </>
              )}

              {/* Signed into the account shell: the workspace/account pages ARE
                  the menu, in the same editorial rows the marketing links use.
                  Dashboard is the emphasized destination (meadow + arrow). */}
              {context === "app" &&
                signedIn &&
                accountLinks.map((l, i) => (
                  <MobileNavLink
                    key={l.key}
                    index={`0${i + 1}`}
                    href={l.href}
                    label={l.label}
                    active={isAccountPath(l.href)}
                    onClick={closeMenu}
                    tone={l.key === "dashboard" ? "meadow" : "default"}
                    arrow={l.key === "dashboard"}
                  />
                ))}

              {/* Pricing — last row. For signed-out visitors it's the menu's
                  emphasized destination (meadow + arrow, the conversion row);
                  signed in it reads as a plain row — Dashboard carries the
                  emphasis instead. */}
              <MobileNavLink
                index={pricingIndex ?? ""}
                href={pricingPath}
                label={copy.pricing}
                active={active === "pricing"}
                onClick={closeMenu}
                tone={signedIn ? "default" : "meadow"}
                arrow={!signedIn}
              />
            </div>

            {/* Signed in on a marketing page: account shortcuts under their own
                eyebrow — compact rows so the page sections keep top billing,
                but Dashboard & co. are never more than one menu away. */}
            {context === "marketing" && signedIn && (
              <div className="mt-9">
                <p className="font-data text-[0.65rem] uppercase tracking-[0.22em] text-brand-muted mb-3">
                  {copy.yourAccount}
                </p>
                <div className={`border-t ${HAIRLINE}`}>
                  {accountLinks.map((l) => (
                    <MobileAccountLink
                      key={l.key}
                      href={l.href}
                      label={l.label}
                      active={isAccountPath(l.href)}
                      onClick={closeMenu}
                      emphasis={l.key === "dashboard"}
                    />
                  ))}
                </div>
              </div>
            )}

            {/* Bottom cluster: identity (signed in), language + sign in/out,
                then the primary CTA. */}
            <div className="mt-auto pt-10">
              {signedIn && (
                <div className={`flex items-center gap-3 border-t ${HAIRLINE} pt-5 mb-6`}>
                  <span
                    aria-hidden
                    className="grid h-9 w-9 flex-shrink-0 place-items-center rounded-md border border-brand-border bg-brand-moss/30 font-data text-[0.7rem] text-brand-meadow"
                  >
                    {initials}
                  </span>
                  <div className="min-w-0">
                    <p className="truncate text-[0.9rem] text-brand-linen leading-tight">{displayName}</p>
                    {user?.email && displayName !== user.email && (
                      <p className="truncate text-[0.75rem] text-brand-muted leading-tight mt-0.5">{user.email}</p>
                    )}
                  </div>
                </div>
              )}
              {/* Theme — same row grammar as Language below. Anonymous
                  visitors have no other toggle. */}
              <div className="flex items-center gap-3 mb-5">
                <span className="font-data text-[0.65rem] uppercase tracking-[0.22em] text-brand-muted">
                  {copy.theme}
                </span>
                <ThemeToggle />
              </div>
              <div className="flex items-center justify-between mb-5">
                <div className="flex items-center gap-3">
                  <span className="font-data text-[0.65rem] uppercase tracking-[0.22em] text-brand-muted">
                    {copy.language}
                  </span>
                  <LangSwitch locale={locale} altHref={altHref} size="lg" />
                </div>
                {signedIn ? (
                  <button
                    type="button"
                    onClick={() => {
                      setMenuOpen(false);
                      void signOut();
                    }}
                    className="py-3 -my-3 text-[0.95rem] text-brand-sage hover:text-brand-linen transition-colors"
                  >
                    {copy.signOut}
                  </button>
                ) : (
                  <Link
                    href="/signin"
                    onClick={closeMenu}
                    className="py-3 -my-3 text-[0.95rem] text-brand-sage hover:text-brand-linen transition-colors"
                  >
                    {copy.signIn}
                  </Link>
                )}
              </div>
              <Link
                href={signedIn ? appPath : START_HREF}
                onClick={closeMenu}
                className="flex w-full items-center justify-center px-5 py-3.5 bg-brand-meadow text-brand-forest text-[0.95rem] font-medium rounded-md hover:bg-brand-meadow-light active:scale-[0.98] transition-all"
              >
                {signedIn ? copy.ctaSignedIn : copy.cta}
              </Link>
            </div>
          </div>
        </div>
      )}
    </nav>
  );
}

/* ── Sub-components ── */

function NavItem({
  href,
  label,
  active,
  onClick,
}: {
  href: string;
  label: string;
  active: boolean;
  onClick?: React.MouseEventHandler<HTMLAnchorElement>;
}) {
  return (
    <Link
      href={href}
      onClick={onClick}
      className={`hidden md:inline text-sm transition-colors ${
        active
          ? "text-brand-linen"
          : "text-brand-sage hover:text-brand-linen"
      }`}
    >
      {label}
    </Link>
  );
}

/** Large serif row in the mobile panel — generous tap target, hairline divider.
 *  tone="meadow" + arrow marks the ONE emphasized destination of the menu:
 *  Pricing for signed-out visitors, Dashboard for signed-in members. */
function MobileNavLink({
  index,
  href,
  label,
  active,
  onClick,
  tone = "default",
  arrow = false,
}: {
  index: string;
  href: string;
  label: string;
  active: boolean;
  onClick?: React.MouseEventHandler<HTMLAnchorElement>;
  tone?: "default" | "meadow";
  arrow?: boolean;
}) {
  const meadow = tone === "meadow";
  return (
    <Link
      href={href}
      onClick={onClick}
      aria-current={active ? "page" : undefined}
      className={`flex items-center justify-between gap-4 py-4 border-b ${HAIRLINE} group`}
    >
      <span className="flex items-baseline gap-4">
        {index && (
          <span
            aria-hidden
            className={`font-data text-[0.65rem] tracking-[0.12em] w-5 ${
              meadow ? "text-brand-meadow/70" : "text-brand-muted"
            }`}
          >
            {index}
          </span>
        )}
        <span
          className={`font-display text-[1.75rem] leading-tight tracking-[0.01em] transition-colors ${
            meadow
              ? "font-semibold text-brand-meadow"
              : `font-medium ${active ? "text-brand-linen" : "text-brand-linen/85 group-hover:text-brand-linen"}`
          }`}
        >
          {label}
        </span>
      </span>
      {arrow && (
        <svg
          aria-hidden
          width="18"
          height="18"
          viewBox="0 0 18 18"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.25"
          className={`flex-shrink-0 transition-colors ${
            meadow ? "text-brand-meadow/70 group-hover:text-brand-meadow" : "text-brand-muted group-hover:text-brand-sage"
          }`}
        >
          <path d="M3 9h12M10 4l5 5-5 5" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      )}
    </Link>
  );
}

/** Compact account row for the marketing panel's "Your account" group —
 *  same editorial voice as MobileNavLink, one step down in scale.
 *  `emphasis` marks Dashboard as the group's meadow destination. */
function MobileAccountLink({
  href,
  label,
  active,
  onClick,
  emphasis = false,
}: {
  href: string;
  label: string;
  active: boolean;
  onClick?: React.MouseEventHandler<HTMLAnchorElement>;
  emphasis?: boolean;
}) {
  return (
    <Link
      href={href}
      onClick={onClick}
      aria-current={active ? "page" : undefined}
      className={`flex items-center justify-between gap-4 py-3.5 border-b ${HAIRLINE} group`}
    >
      <span
        className={`font-display text-[1.2rem] leading-tight tracking-[0.01em] transition-colors ${
          emphasis
            ? "font-semibold text-brand-meadow"
            : `font-medium ${active ? "text-brand-linen" : "text-brand-linen/85 group-hover:text-brand-linen"}`
        }`}
      >
        {label}
      </span>
      <svg
        aria-hidden
        width="14"
        height="14"
        viewBox="0 0 18 18"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.25"
        className={`flex-shrink-0 transition-colors ${
          emphasis
            ? "text-brand-meadow/70 group-hover:text-brand-meadow"
            : "text-brand-muted group-hover:text-brand-sage"
        }`}
      >
        <path d="M3 9h12M10 4l5 5-5 5" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    </Link>
  );
}

function LangSwitch({
  locale,
  altHref,
  className = "flex",
  size = "sm",
}: {
  locale: "en" | "nl";
  altHref: string;
  className?: string;
  size?: "sm" | "lg";
}) {
  // Persist choice so a refresh honours it, matching the landing's existing behaviour.
  const persist = (l: "en" | "nl") => {
    try { localStorage.setItem("plotscore-lang", l); } catch {}
  };
  const base =
    size === "lg"
      ? "px-3 py-1.5 rounded-[4px] font-data text-[0.75rem] tracking-[0.08em] transition-colors"
      : "px-2 py-0.5 rounded-[4px] font-data text-[0.7rem] tracking-[0.08em] transition-colors";
  const active = "text-brand-linen bg-brand-meadow/15";
  const idle = "text-brand-muted hover:text-brand-sage";
  return (
    <div className={`${className} items-center gap-1 border border-brand-border rounded-md p-0.5`}>
      {locale === "en" ? (
        <span aria-pressed className={`${base} ${active}`}>EN</span>
      ) : (
        <Link href={altHref} onClick={() => persist("en")} className={`${base} ${idle}`}>EN</Link>
      )}
      {locale === "nl" ? (
        <span aria-pressed className={`${base} ${active}`}>NL</span>
      ) : (
        <Link href={altHref} onClick={() => persist("nl")} className={`${base} ${idle}`}>NL</Link>
      )}
    </div>
  );
}
